/**
 * Authenticated fetch wrapper for the Broke or Not API.
 *
 * Pattern 4 from RESEARCH.md: Calls fetchAuthSession() to get the Cognito
 * access token and attaches it as a Bearer header on every request.
 *
 * Uses the native fetch API (not axios or other HTTP libs) to keep this
 * module RN-reusable (CLAUDE.md: fetch-based api-client for RN reuse).
 * No DOM-specific imports.
 *
 * Target: VITE_API_URL from apps/web/.env.local (Pitfall 9 — VITE_ prefix required).
 */
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { useImpersonationStore } from '@/features/admin/impersonationStore';

const API_URL = import.meta.env.VITE_API_URL as string;

/**
 * Thrown when no usable Cognito session can be obtained — e.g. the cached tokens
 * are expired and the refresh token is invalid (the canonical case: the user was
 * deleted in Cognito but the browser still holds their stale tokens).
 *
 * Distinct from a generic network/data error so callers (and the global handler
 * below) can drive the user back to sign-in instead of showing a misleading
 * "could not load your data" message on top of a half-authenticated shell.
 */
export class AuthSessionExpiredError extends Error {
  constructor(message = 'Your session has expired. Please sign in again.') {
    super(message);
    this.name = 'AuthSessionExpiredError';
  }
}

/**
 * Resolve a usable Cognito access token.
 *
 * Why forceRefresh: getCurrentUser()/fetchAuthSession() return DECODED cached
 * tokens without validating them server-side. After "delete all users + wipe
 * data", the browser can hold expired access tokens for a now-deleted user under
 * the same app-client storage key. A plain fetchAuthSession() either returns an
 * expired token (rejected by the API) or, once it tries to refresh against the
 * deleted user, returns no token at all. We force a refresh so we either get a
 * genuinely valid token or a definitive "no session" — never a stale one.
 */
async function getValidAccessToken(): Promise<string> {
  // First attempt: normal session (uses cache, auto-refreshes if merely expired).
  let token = (await fetchAuthSession()).tokens?.accessToken?.toString();
  if (token) return token;

  // Fallback: force a refresh in case the cached session was stale/half-populated.
  try {
    token = (await fetchAuthSession({ forceRefresh: true })).tokens?.accessToken?.toString();
  } catch {
    token = undefined;
  }
  if (token) return token;

  // No usable session. Clear the broken/stale tokens so the app stops presenting
  // a half-authenticated state, then signal callers to re-authenticate.
  try {
    await signOut();
  } catch {
    /* best-effort — signOut can fail if there is nothing to sign out of */
  }
  throw new AuthSessionExpiredError();
}

/**
 * Make an authenticated API request.
 * Automatically attaches the Cognito access token as Authorization: Bearer <token>.
 *
 * @param path - API path (e.g. '/balance') — will be appended to VITE_API_URL
 * @param method - HTTP method ('GET' | 'PUT' | 'POST' | 'DELETE')
 * @param body - Optional request body (will be JSON-serialized)
 * @returns Raw Response object — callers handle .json() and status checking
 */
export async function apiRequest(
  path: string,
  method: 'GET' | 'PUT' | 'POST' | 'DELETE',
  body?: unknown
): Promise<Response> {
  const accessToken = await getValidAccessToken();

  const headers: HeadersInit = {
    Authorization: `Bearer ${accessToken}`,
    'Content-Type': 'application/json',
  };

  // Admin view-only impersonation (quick-260702-qbr): attach the target sub when an admin
  // has selected a user via AdminUserPicker. getState() (non-hook) is safe outside React.
  // Non-admins never have targetSub set (the picker only renders for admins), and the
  // backend ignores this header for any non-admin caller regardless.
  const targetSub = useImpersonationStore.getState().targetSub;
  if (targetSub) {
    (headers as Record<string, string>)['X-Impersonate-User'] = targetSub;
  }

  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // A 401 here means the token we sent was rejected by the JWT authorizer despite
  // looking valid locally (e.g. a stale token whose user no longer exists). Treat
  // it as an expired session: clear it and route the user back to sign-in rather
  // than surfacing a generic data-load failure.
  if (response.status === 401) {
    try {
      await signOut();
    } catch {
      /* best-effort */
    }
    throw new AuthSessionExpiredError();
  }

  return response;
}
